import React, { useState, useEffect } from 'react';
import { Calculator, Users, Building2, Save, History, X } from 'lucide-react';

// Hooks
import { useEmployees } from '../../hooks/useEmployees';
import { useDepartments } from '../../hooks/useDepartments';
import { usePayrolls } from './hooks/usePayrolls';
import { usePrestaciones } from '../../hooks/usePrestaciones';
import { useCompany } from '../../contexts/CompanyContext';

import { prestacionesAPI } from '../../api/prestaciones';

// Components
import EmployeeSelector from './components/EmployeeSelector';
import IndividualPrestacionesView from './components/IndividualPrestacionesView';
import MultiplePrestacionesView from './components/MultiplePrestacionesView';
import PrestacionesHistory from './components/PrestacionesHistory';

export default function PrestacionesModule() {
  // Data hooks
  const { currentCompany } = useCompany();
  const { employees, loading: loadingEmployees } = useEmployees();
  const { departments, loading: loadingDepartments } = useDepartments();
  const { payrolls, loading: loadingPayrolls } = usePayrolls();
  const {
    prestaciones,
    loading: loadingPrestaciones,
    error: errorPrestaciones,
    addPrestacion,
    deletePrestacion,
    markAsPaid
  } = usePrestaciones();

  // State
  const [scope, setScope] = useState('individual');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [empleadoTomoVacaciones, setEmpleadoTomoVacaciones] = useState(false);

  // Custom dates
  const [usarFechasPersonalizadas, setUsarFechasPersonalizadas] = useState(false);
  const [fechaIngresoPersonalizada, setFechaIngresoPersonalizada] = useState('');
  const [fechaSalidaPersonalizada, setFechaSalidaPersonalizada] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Horas extras y comisiones
  const [horasExtrasPendientes, setHorasExtrasPendientes] = useState('0');
  const [comisionesPendientes, setComisionesPendientes] = useState('0');

  // Opciones de cálculo
  const [incluirPreaviso, setIncluirPreaviso] = useState(true);
  const [incluirCesantia, setIncluirCesantia] = useState(true);

  // Salarios de referencia desde nóminas reales
  const [salarioMesAnterior, setSalarioMesAnterior] = useState(null);
  const [salarioPromedio12, setSalarioPromedio12] = useState(null);

  // Results
  const [prestacionesData, setPrestacionesData] = useState(null);
  const [multipleData, setMultipleData] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [calculatingMultiple, setCalculatingMultiple] = useState(false);
  const [multipleError, setMultipleError] = useState(null);

  // UI State
  const [showHistory, setShowHistory] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Loading state
  const loading = loadingEmployees || loadingDepartments || loadingPayrolls;

  // Reset when changing scope
  useEffect(() => {
    setSelectedEmployee('');
    setSelectedDepartment('');
    setPrestacionesData(null);
    setMultipleData(null);
    setEmpleadoTomoVacaciones(false);
    setHorasExtrasPendientes('0');
    setComisionesPendientes('0');
    setUsarFechasPersonalizadas(false);
    setFechaIngresoPersonalizada('');
    setFechaSalidaPersonalizada(new Date().toISOString().split('T')[0]);
    setSalarioMesAnterior(null);
    setSalarioPromedio12(null);
  }, [scope]);

  // Obtener salarios de referencia desde nóminas reales al seleccionar empleado
  useEffect(() => {
    if (selectedEmployee && currentCompany?.id) {
      prestacionesAPI.getSalarioMesAnterior(currentCompany.id, selectedEmployee)
        .then(data => {
          setSalarioMesAnterior(data.salarioCotizable ?? null);
          setSalarioPromedio12(data.salarioPromedio12 ?? null);
        })
        .catch(() => {
          setSalarioMesAnterior(null);
          setSalarioPromedio12(null);
        });
    } else {
      setSalarioMesAnterior(null);
      setSalarioPromedio12(null);
    }
  }, [selectedEmployee, currentCompany?.id]);

  // Calcular prestaciones individuales en el backend (con debounce)
  useEffect(() => {
    if (scope !== 'individual' || !selectedEmployee || !currentCompany?.id) {
      setPrestacionesData(null);
      setCalcError(null);
      return;
    }

    setCalculating(true);
    setCalcError(null);

    const timer = setTimeout(async () => {
      try {
        const data = await prestacionesAPI.calcular({
          employeeId:           selectedEmployee,
          fechaIngresoOverride: usarFechasPersonalizadas && fechaIngresoPersonalizada ? fechaIngresoPersonalizada : null,
          fechaSalidaOverride:  usarFechasPersonalizadas && fechaSalidaPersonalizada  ? fechaSalidaPersonalizada  : null,
          tomoVacaciones:       empleadoTomoVacaciones,
          incluirPreaviso,
          incluirCesantia,
          horasExtrasPendientes: parseFloat(horasExtrasPendientes) || 0,
          comisionesPendientes:  parseFloat(comisionesPendientes)  || 0,
        });
        setPrestacionesData(data);
      } catch (err) {
        setPrestacionesData(null);
        setCalcError(err.response?.data?.error || err.message || 'Error al calcular');
      } finally {
        setCalculating(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [scope, selectedEmployee, currentCompany?.id, empleadoTomoVacaciones, usarFechasPersonalizadas, fechaIngresoPersonalizada, fechaSalidaPersonalizada, incluirPreaviso, incluirCesantia, horasExtrasPendientes, comisionesPendientes]); // eslint-disable-line

  // Calcular prestaciones para departamento/organización en el backend
  useEffect(() => {
    const isReady = (scope === 'departamento' && selectedDepartment) || scope === 'organizacion';
    if (!isReady || !currentCompany?.id || employees.length === 0) {
      setMultipleData(null);
      setMultipleError(null);
      return;
    }

    let filtered = employees.filter(e => e.estado === 'activo');
    if (scope === 'departamento' && selectedDepartment) {
      filtered = filtered.filter(e => e.departamento === selectedDepartment);
    }
    if (filtered.length === 0) {
      setMultipleData({ empleados: [], totales: { empleados: 0, vacaciones: 0, salarioNavidad: 0, preaviso: 0, cesantia: 0, total: 0 }, errors: [] });
      return;
    }

    const employeeIds = filtered.map(e => e.id);
    const fechaIngresoOverride = usarFechasPersonalizadas && fechaIngresoPersonalizada ? fechaIngresoPersonalizada : null;
    const fechaSalidaOverride  = usarFechasPersonalizadas && fechaSalidaPersonalizada  ? fechaSalidaPersonalizada  : null;

    setCalculatingMultiple(true);
    setMultipleError(null);

    const timer = setTimeout(async () => {
      try {
        const data = await prestacionesAPI.calcularMultiple({ employeeIds, fechaIngresoOverride, fechaSalidaOverride });
        setMultipleData(data);
      } catch (err) {
        setMultipleData(null);
        setMultipleError(err.response?.data?.error || err.message || 'Error al calcular');
      } finally {
        setCalculatingMultiple(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [scope, selectedDepartment, employees, currentCompany?.id, usarFechasPersonalizadas, fechaIngresoPersonalizada, fechaSalidaPersonalizada]); // eslint-disable-line

  // Guardar prestación — solo envía parámetros, el backend recalcula y guarda
  const handleSavePrestacion = async () => {
    if (!prestacionesData || !currentCompany?.id) return;

    try {
      await addPrestacion({
        employeeId:           selectedEmployee,
        fechaIngresoOverride: usarFechasPersonalizadas && fechaIngresoPersonalizada ? fechaIngresoPersonalizada : null,
        fechaSalidaOverride:  usarFechasPersonalizadas && fechaSalidaPersonalizada  ? fechaSalidaPersonalizada  : null,
        tomoVacaciones:       empleadoTomoVacaciones,
        incluirPreaviso,
        incluirCesantia,
        horasExtrasPendientes: parseFloat(horasExtrasPendientes) || 0,
        comisionesPendientes:  parseFloat(comisionesPendientes)  || 0,
      });
      setShowSaveDialog(false);
    } catch (error) {
      console.error('Error al guardar prestación:', error);
      alert('❌ Error al guardar: ' + (error.response?.data?.error || error.message || 'Error desconocido'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header con scope selector inline */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Scope tabs */}
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {[
              { id: 'individual', label: 'Individual', icon: Calculator },
              { id: 'departamento', label: 'Depto.', icon: Building2 },
              { id: 'organizacion', label: 'Organización', icon: Users },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setScope(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  scope === id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <History className="w-4 h-4" />
            Historial
          </button>
        </div>
      </div>

      {/* Historial Modal */}
      {showHistory && (
        <PrestacionesHistory
          prestaciones={prestaciones || []}
          employees={employees}
          onClose={() => setShowHistory(false)}
          onDelete={async (id) => {
            try {
              await deletePrestacion(id);
              alert('✅ Prestación eliminada');
            } catch (error) {
              alert('❌ Error al eliminar: ' + error.message);
            }
          }}
          onMarkAsPaid={async (id) => {
            try {
              await markAsPaid(id);
              alert('✅ Marcada como pagada');
            } catch (error) {
              alert('❌ Error al marcar como pagada: ' + error.message);
            }
          }}
        />
      )}

      {/* ─── INDIVIDUAL: layout 2 paneles ─── */}
      {scope === 'individual' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

          {/* Panel izquierdo: configuración */}
          <div className="lg:col-span-2 space-y-4">
            <EmployeeSelector
              employees={employees}
              selectedEmployee={selectedEmployee}
              onEmployeeChange={setSelectedEmployee}
              empleadoTomoVacaciones={empleadoTomoVacaciones}
              onTomoVacacionesChange={setEmpleadoTomoVacaciones}
              usarFechasPersonalizadas={usarFechasPersonalizadas}
              onUsarFechasPersonalizadasChange={setUsarFechasPersonalizadas}
              fechaIngresoPersonalizada={fechaIngresoPersonalizada}
              onFechaIngresoChange={setFechaIngresoPersonalizada}
              fechaSalidaPersonalizada={fechaSalidaPersonalizada}
              onFechaSalidaChange={setFechaSalidaPersonalizada}
              incluirPreaviso={incluirPreaviso}
              onIncluirPreavisoChange={setIncluirPreaviso}
              incluirCesantia={incluirCesantia}
              onIncluirCesantiaChange={setIncluirCesantia}
            />

            {/* Conceptos pendientes */}
            {selectedEmployee && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Conceptos Pendientes</h3>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Horas Extras (RD$)</label>
                  <input
                    type="number"
                    value={horasExtrasPendientes}
                    onChange={(e) => setHorasExtrasPendientes(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Comisiones (RD$)</label>
                  <input
                    type="number"
                    value={comisionesPendientes}
                    onChange={(e) => setComisionesPendientes(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            )}

            {/* Guardar */}
            {selectedEmployee && prestacionesData && (
              <button
                onClick={() => setShowSaveDialog(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                Guardar Prestación
              </button>
            )}
          </div>

          {/* Panel derecho: resultados */}
          <div className="lg:col-span-3">
            {selectedEmployee && calculating ? (
              <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-20 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Calculando...</p>
              </div>
            ) : selectedEmployee && calcError ? (
              <div className="bg-white rounded-xl border border-red-200 flex flex-col items-center justify-center py-20 text-center px-6">
                <Calculator className="w-10 h-10 text-red-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-red-600">{calcError}</p>
              </div>
            ) : selectedEmployee && prestacionesData ? (
              <IndividualPrestacionesView
                prestaciones={prestacionesData}
                fechaSalidaPersonalizada={fechaSalidaPersonalizada}
                usarFechasPersonalizadas={usarFechasPersonalizadas}
                horasExtrasPendientes={horasExtrasPendientes}
                comisionesPendientes={comisionesPendientes}
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-20 text-center">
                <Calculator className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500">Selecciona un empleado para ver el cálculo</p>
                <p className="text-xs text-gray-400 mt-1">Busca por nombre o cédula en el panel izquierdo</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── DEPARTAMENTO ─── */}
      {scope === 'departamento' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Departamento</label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:border-blue-400 outline-none text-sm"
              >
                <option value="">Seleccionar departamento</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.nombre}>
                    {dept.nombre} ({employees.filter(e => e.departamento === dept.nombre && e.estado === 'activo').length} empleados)
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={usarFechasPersonalizadas}
                onChange={(e) => setUsarFechasPersonalizadas(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Usar fechas personalizadas</span>
            </label>

            {usarFechasPersonalizadas && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Fecha Ingreso</label>
                  <input type="date" value={fechaIngresoPersonalizada} onChange={(e) => setFechaIngresoPersonalizada(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Fecha Salida</label>
                  <input type="date" value={fechaSalidaPersonalizada} onChange={(e) => setFechaSalidaPersonalizada(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                    max={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
            )}
          </div>

          {calculatingMultiple ? (
            <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-20 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Calculando...</p>
            </div>
          ) : multipleError ? (
            <div className="bg-white rounded-xl border border-red-200 flex flex-col items-center justify-center py-20 text-center px-6">
              <p className="text-sm font-medium text-red-600">{multipleError}</p>
            </div>
          ) : multipleData ? (
            <MultiplePrestacionesView
              data={multipleData}
              scope={scope}
              scopeName={selectedDepartment}
              departments={departments}
              employees={employees}
              usarFechasPersonalizadas={usarFechasPersonalizadas}
              fechaIngresoPersonalizada={fechaIngresoPersonalizada}
              fechaSalidaPersonalizada={fechaSalidaPersonalizada}
            />
          ) : !selectedDepartment && (
            <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
              <Building2 className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">Selecciona un departamento</p>
            </div>
          )}
        </div>
      )}

      {/* ─── ORGANIZACIÓN ─── */}
      {scope === 'organizacion' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-50 p-2 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Toda la Organización</p>
                <p className="text-xs text-gray-400">{employees.filter(e => e.estado === 'activo').length} empleados activos</p>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={usarFechasPersonalizadas}
                onChange={(e) => setUsarFechasPersonalizadas(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Usar fechas personalizadas</span>
            </label>

            {usarFechasPersonalizadas && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Fecha Ingreso</label>
                  <input type="date" value={fechaIngresoPersonalizada} onChange={(e) => setFechaIngresoPersonalizada(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Fecha Salida</label>
                  <input type="date" value={fechaSalidaPersonalizada} onChange={(e) => setFechaSalidaPersonalizada(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none"
                    max={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
            )}
          </div>

          {calculatingMultiple ? (
            <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-20 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Calculando...</p>
            </div>
          ) : multipleError ? (
            <div className="bg-white rounded-xl border border-red-200 flex flex-col items-center justify-center py-20 text-center px-6">
              <p className="text-sm font-medium text-red-600">{multipleError}</p>
            </div>
          ) : multipleData && (
            <MultiplePrestacionesView
              data={multipleData}
              scope={scope}
              scopeName="Organización"
              departments={departments}
              employees={employees}
              usarFechasPersonalizadas={usarFechasPersonalizadas}
              fechaIngresoPersonalizada={fechaIngresoPersonalizada}
              fechaSalidaPersonalizada={fechaSalidaPersonalizada}
            />
          )}
        </div>
      )}

      {/* Diálogo de confirmación guardar */}
      {showSaveDialog && prestacionesData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Guardar Prestación</h3>
              <button onClick={() => setShowSaveDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              ¿Deseas guardar la prestación calculada para <strong>{employees.find(e => e.id === selectedEmployee)?.nombre}</strong>?
            </p>

            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Prestaciones:</span>
                <span className="font-semibold text-gray-900">
                  RD$ {prestacionesData.totalPrestaciones.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </span>
              </div>
              {parseFloat(horasExtrasPendientes) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Horas Extras:</span>
                  <span className="font-semibold text-gray-900">
                    RD$ {parseFloat(horasExtrasPendientes).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {parseFloat(comisionesPendientes) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Comisiones:</span>
                  <span className="font-semibold text-gray-900">
                    RD$ {parseFloat(comisionesPendientes).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-sm font-semibold text-gray-900">Total:</span>
                <span className="text-base font-bold text-blue-700">
                  RD$ {(prestacionesData.totalPrestaciones + (parseFloat(horasExtrasPendientes) || 0) + (parseFloat(comisionesPendientes) || 0)).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePrestacion}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
